/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import { useEffect } from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import Lenis from "lenis";
import { Layout } from "./components/Layout";
import { Home } from "./pages/Home";
import { About } from "./pages/About";
import { Discography } from "./pages/Discography";
import { Apparatus } from "./pages/Apparatus";
import { Contact } from "./pages/Contact";
import { Services } from "./pages/Services";
import { NotFound } from "./pages/NotFound";
import { ScrollToTop } from "./components/ScrollToTop";
import { AnalyticsObserver } from "./components/AnalyticsObserver";

export default function App() {
  useEffect(() => {
    const isApple = /Mac|iPhone|iPod|iPad/.test(navigator.platform) || 
                    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
    document.documentElement.classList.add(isApple ? 'os-apple' : 'os-pc');

    const isLighthouse = /Chrome-Lighthouse|PageSpeed|Speed Insights|PTST|HeadlessChrome/i.test(navigator.userAgent);
    const isFinePointer = window.matchMedia('(pointer: fine)').matches;
    if (isLighthouse || !isFinePointer) {
      return;
    }

    // Initialize Lenis smooth scrolling on desktop
    const lenis = new Lenis({
      duration: 1.2,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      orientation: 'vertical',
      gestureOrientation: 'vertical',
      smoothWheel: true,
      wheelMultiplier: 1,
      infinite: false,
    });

    let rafId = 0;
    function raf(time: number) {
      lenis.raf(time);
      rafId = requestAnimationFrame(raf);
    }

    rafId = requestAnimationFrame(raf);

    return () => {
      cancelAnimationFrame(rafId);
      lenis.destroy();
    };
  }, []);

  return (
    <BrowserRouter>
      <ScrollToTop />
      <AnalyticsObserver />
      <Routes>
        <Route path="/" element={<Layout />}>
          <Route index element={<Home />} />
          <Route path="about" element={<About />} />
          <Route path="services" element={<Services />} />
          <Route path="apparatus" element={<Apparatus />} />
          <Route path="discography" element={<Discography />} />
          <Route path="contact" element={<Contact />} />
          <Route path="*" element={<NotFound />} />
        </Route>
      </Routes>
    </BrowserRouter>
  );
}
